import _ from 'underscore';

import * as EventConstants from 'constants/EventConstants';
import AppDispatcher from 'lib/Dispatcher';
import notifications, {isResponse} from 'notifications';

import * as ExportDAO from './ExportDAO';

const GENERIC_EXPORT_ERROR_MESSAGE =
  'An error occurred while performing export. Please try again or reach out to support@benchling.com if the issue persists.';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _onExportTaskRunnerError = (e: any, notificationId: string): void => {
  if (isResponse(e)) {
    // notifications.notifyError call does not close the notification of same id
    notifications.close(notificationId);
    notifications.notifyError(e, {id: notificationId});
  } else {
    notifications.error(GENERIC_EXPORT_ERROR_MESSAGE, {id: notificationId});
  }
};

async function requestSingleExportAndNotify(
  requestExportFn: () => Promise<void>,
  fileId: string
): Promise<void> {
  const notificationId = `request-export-${fileId}`;
  notifications.info('Running export...', {id: notificationId, duration: 0});
  try {
    await requestExportFn();
    notifications.success(`Your export has completed. You will receive an email shortly.`, {
      id: notificationId,
    });
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    _onExportTaskRunnerError(e, notificationId);
  }
}

async function requestMultiExportAndNotify(requestExportFn: () => Promise<void>): Promise<void> {
  const notificationId = _.uniqueId('request-export-');
  notifications.info('Starting export...', {id: notificationId, duration: 0});
  try {
    await requestExportFn();
    notifications.success(
      `Your export is now running, and may take some time. You will receive an email when it is complete.`,
      {id: notificationId}
    );
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    _onExportTaskRunnerError(e, notificationId);
  }
}

const ExportActions = {
  requestExportFile: (fileId: string): Promise<void> =>
    requestSingleExportAndNotify(() => ExportDAO.requestExportFile(fileId), fileId),

  requestBulkExportFiles: (fileIds: Array<string>, exportType: string, format: string): Promise<void> =>
    requestMultiExportAndNotify(() => ExportDAO.requestBulkExportFiles(fileIds, exportType, format)),

  requestExportFolders: (
    folderIds: Array<string>,
    exportType: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any
  ): Promise<void> =>
    requestMultiExportAndNotify(() => ExportDAO.requestExportFolders(folderIds, exportType, data)),

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  requestPatentsafeExport: (fileId: string): JQueryPromise<any> => {
    AppDispatcher.dispatch({
      actionType: EventConstants.EXPORT_PATENTSAFE_STARTED,
      fileId,
    });
    notifications.success(`Your file is being saved as a PDF and will be sent to PatentSafe. You will be \
    notified when it has finished.`);

    return ExportDAO.requestPatentsafeExportTask(fileId)
      .done((data) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.EXPORT_PATENTSAFE_COMPLETED,
          fileId,
          item: data,
        });
        notifications.success('Your file has been sent to PatentSafe.');
      })
      .fail((error) => {
        notifications.notifyError(error);
        AppDispatcher.dispatch({
          actionType: EventConstants.EXPORT_PATENTSAFE_FAILED,
          fileId,
        });
      });
  },
};

export default ExportActions;
